import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

/* ── MUI Components ─────────────────────────────────────────────── */
import {
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    capitalize,
    ClickAwayListener,
    FormControlLabel,
    Grow,
    IconButton,
    LinearProgress,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    useMediaQuery,
} from '@mui/material';

/* ── MUI Icons ──────────────────────────────────────────────────── */
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LunchDiningIcon from '@mui/icons-material/LunchDining';

/* ── Third-party ────────────────────────────────────────────────── */
import { jsPDF } from 'jspdf';

/* ── App components & utilities ─────────────────────────────────── */
import ConfirmationModal from '../Modals/ConfirmationModal/ConfirmationModal';
import EditDietModal from '../Modals/EditDietModal/EditDietModal';
import AddMealModal from '../Modals/AddMealModal/AddMealModal';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { useAppContext } from '../../context/AppContext';
import { formatDate, getFullDate } from '../../util';
import './DietTable.css';
import { capitalizeFirst } from '../../utils/functions';
import RecipePickerModal from '../Modals/RecipePickerModal/RecipePickerModal';
import TourButton from '../TourButton/TourButton';
import { getDietTableSteps } from '../../tours/dietTable';

/* ═══════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════ */

/** Canonical display order for the eight supported meal types. */
const MEAL_ORDER = [
    'breakfast', 'snack1', 'lunch', 'snack2',
    'dinner', 'snack3', 'preWorkout', 'postWorkout',
];

/**
 * All macro-nutrient keys stored on each food / meal / totals object.
 * Used when summing meal totals and daily totals.
 */
const ALL_MACROS = ['kcal', 'fats', 'carbs', 'sugars', 'fibers', 'proteins', 'salt'];

/**
 * The subset of macros that are recalculated **proportionally** when
 * the user changes the `quantity` value of a food item.
 *
 * Example: if the base food has 100 g / 200 kcal / 10 g fats and the
 * user types 200 g, each of these macros is multiplied by 200 / 100 = 2.
 */
const PROPORTIONAL_MACROS = ['kcal', 'fats', 'proteins', 'carbs'];

/**
 * Builds the shared MUI `sx` object for the compact numeric TextFields
 * (quantity, kcal, fats, carbs, …).  Accepts `appColors` so that focus /
 * hover outlines match the application palette.
 */
const buildNumericFieldSx = (appColors) => ({
    width: '64px',
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: appColors.tertiary },
        '&.Mui-focused fieldset': { borderColor: appColors.secondary },
    },
    '& .MuiInputBase-input': {
        padding: '4px 8px',
        textAlign: 'right',
        fontSize: '0.85rem',
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
        '&[type=number]': { MozAppearance: 'textfield' },
    },
});

/* ═══════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════ */

/**
 * DietTable
 * ─────────
 * Renders a weekly diet plan as an interactive, responsive MUI table.
 *
 * Features
 * --------
 * - **Read mode**: displays all days / meals / foods with daily and meal
 *   totals.  Action buttons allow editing, PDF download, and deletion.
 * - **Edit mode**: every food row becomes editable.  Autocomplete for
 *   food names, numeric inputs for macros.  Meal-level dropdown to add
 *   or delete foods / meals.
 * - **Generation mode**: replaces the table body with a linear progress
 *   bar while the AI is generating the diet.
 *
 * Responsiveness
 * ──────────────
 * | Breakpoint   | Visible columns                                    |
 * |--------------|----------------------------------------------------|
 * | > 1024 px    | All (name, qty, kcal, fats, carbs, sugars,         |
 * |              | fibers, proteins, salt)                             |
 * | ≤ 1024 px    | name, qty, kcal, fats, carbs, proteins             |
 * | ≤ 768 px     | name, qty, fats, carbs, proteins                   |
 *
 * @component
 */
const DietTable = ({
    diet,
    editedDiet,
    editing = false,
    onDietChange,
    refreshDietTable,
    onDeleteDiet,
    client,
    dietGeneration,
    showInfoBar = true,
}) => {
    const { t } = useTranslation();
    const { appColors, currentLanguage, dietist } = useAppContext();
    const isMobile = useMediaQuery('(max-width: 768px)');

    /* ── Local state ──────────────────────────────────────────────── */
    const [loading, setLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(0);

    // Modal visibility flags
    const [showAddMealModal, setShowAddMealModal] = useState(false);
    const [showEditDietModal, setShowEditDietModal] = useState(false);
    const [showDeleteMealConfirmationModal, setShowDeleteMealConfirmationModal] = useState(false);
    const [showDeleteDietConfirmationModal, setShowDeleteDietConfirmationModal] = useState(false);
    const [deleteMealConfirmationModalOptions, setDeleteMealConfirmationModalOptions] = useState({ meal: '', day: '' });
    const [showRecipePicker, setShowRecipePicker] = useState(false);
    const [recipeTarget, setRecipeTarget] = useState({ day: null, meal: null });
    
    // Food autocomplete search results
    const [foodSearchList, setFoodSearchList] = useState([]);

    // Generic brand filter for food autocomplete
    const [onlyGenerics, setOnlyGenerics] = useState(true);

    // Popper anchor elements for the per-meal option menus (Add / Delete)
    const [menuAnchorEls, setMenuAnchorEls] = useState(
        Object.fromEntries(MEAL_ORDER.map((m) => [m, null]))
    );

    /**
     * The working copy of the 7-day diet plan, keyed by `day0` … `day6`.
     * Each day object contains meal keys (from MEAL_ORDER) whose values
     * have a `foods` array and pre-computed meal totals, plus a `totals`
     * object for the whole day.
     */
    const [dietPlan, setDietPlan] = useState({
        day0: {}, day1: {}, day2: {}, day3: {},
        day4: {}, day5: {}, day6: {},
    });

    /**
     * A **frozen snapshot** of the diet plan taken right after parsing.
     *
     * PURPOSE — Proportional macro recalculation
     * ─────────────────────────────────────────────
     * When the user edits the `quantity` field of a food item, we do NOT want
     * to recalculate macros based on the *current* (possibly already
     * modified) values — that would cause compounding rounding errors.
     *
     * Instead we keep this independent deep copy (`baseMacrosFoodProportion`)
     * that always reflects the **original** per-food macro values from the
     * server.  The recalculation formula is:
     *
     *     newMacro = baseMacro × (newQuantity / baseQuantity)
     *
     * This guarantees that the proportions remain consistent no matter how
     * many times the user changes the quantity value back and forth.
     *
     * IMPORTANT: both `dietPlan` and `baseMacrosFoodProportion` must stay
     * structurally identical (same days / meals / food indices).  When
     * adding or removing foods/meals, **both** states must be updated in
     * lockstep.
     */
    const [baseMacrosFoodProportion, setBaseMacrosFoodProportion] = useState({
        day0: {}, day1: {}, day2: {}, day3: {},
        day4: {}, day5: {}, day6: {},
    });

    /** Localised weekday names (lowercase). */
    const weekDayNames = [
        t('Monday'), t('Tuesday'), t('Wednesday'), t('Thursday'),
        t('Friday'), t('Saturday'), t('Sunday'),
    ].map((d) => d.toLowerCase());

    /** Pre-compute the numeric field sx once per render. */
    const numFieldSx = buildNumericFieldSx(appColors);
    const tourSteps = useMemo(() => getDietTableSteps(t), [t]);

    /* ═════════════════════════════════════════════════════════════════
       PARSE DIET DATA → LOCAL STATE
       ═════════════════════════════════════════════════════════════════ */

    /**
     * Parses the raw `diet.dietPlan` object received from the server
     * into our local `dietPlan` state.
     *
     * For every day → every meal → every food:
     *   1. Deep-clone the food items (so mutations don't affect props).
     *   2. Sum each macro across foods to produce meal-level totals.
     *   3. Sum meal totals to produce a day-level `totals` object.
     *
     * After parsing, we store **two independent deep copies**:
     * - `dietPlan`                – the working copy the UI reads/writes.
     * - `baseMacrosFoodProportion` – the frozen snapshot used for
     *   proportional recalculation (see state declaration above).
     */
    useEffect(() => {
        const parseDiet = () => {
            const parsedDietPlan = {};

            for (const [day, dailyDiet] of Object.entries(diet.dietPlan)) {
                const parsedDailyDiet = {};
                const dailyTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));

                MEAL_ORDER.forEach((meal) => {
                    if (!dailyDiet[meal]) return;

                    const mealInfo = dailyDiet[meal];
                    const parsedMealInfo = { ...mealInfo };
                    const mealTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));

                    // Deep-clone each food and accumulate meal totals
                    parsedMealInfo.foods = mealInfo.foods.map((food) => {
                        ALL_MACROS.forEach((macro) => {
                            mealTotals[macro] += food[macro] ?? 0;
                        });
                        return { ...food };
                    });

                    // Write meal totals onto the meal object
                    ALL_MACROS.forEach((macro) => {
                        parsedMealInfo[macro] = mealTotals[macro];
                    });

                    parsedDailyDiet[meal] = parsedMealInfo;
                });

                // Accumulate daily totals from all parsed meals
                Object.values(parsedDailyDiet).forEach((meal) => {
                    ALL_MACROS.forEach((macro) => {
                        dailyTotals[macro] += meal[macro] ?? 0;
                    });
                });

                parsedDailyDiet.totals = dailyTotals;
                parsedDietPlan[day] = parsedDailyDiet;
            }

            // Create two independent deep copies so editing one doesn't affect the other
            setBaseMacrosFoodProportion(JSON.parse(JSON.stringify(parsedDietPlan)));
            setDietPlan(parsedDietPlan);
        };

        if (!dietGeneration && diet?.dietPlan && Object.keys(diet.dietPlan).length > 0) {
            parseDiet();
        }
    }, [diet, dietGeneration]);

    /* ── Food search ──────────────────────────────────────────────── */

    /** Fetches food items whose name matches `foodName` for the Autocomplete dropdown. */
    const getFoodsFilteredByName = async (foodName) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${foodName}&language=${currentLanguage}${onlyGenerics ? '&onlyGenerics=true' : ''}`;
        const response = await axios.get(url, { withCredentials: true });
        setFoodSearchList(response.data);
    };

    /**
     * Handles input-text changes inside the food-name Autocomplete.
     *
     * - When the input becomes empty (user deleted all characters or clicked
     *   clear): clears the search list AND resets the food row's macros to 0.
     * - Otherwise: fetches foods matching the typed text.
     */
    const handleFoodInputChange = (event, newInputValue, reason, day, meal, index) => {
        if (reason !== 'input' && reason !== 'clear') return;

        if (!newInputValue) {
            setFoodSearchList([]);

            // Reset the food item to a blank row
            const blankFood = {
                name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0,
                carbs: 0, sugars: 0, fibers: 0, salt: 0,
            };
            const foodListCopy = [...dietPlan[day][meal].foods];
            foodListCopy[index] = blankFood;

            const newDietPlan = { ...dietPlan };
            newDietPlan[day][meal] = { ...newDietPlan[day][meal], foods: foodListCopy };

            ALL_MACROS.forEach((macro) => {
                newDietPlan[day][meal][macro] = foodListCopy.reduce(
                    (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
                );
            });

            const dailyTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));
            Object.keys(newDietPlan[day]).forEach((key) => {
                if (key === 'totals') return;
                ALL_MACROS.forEach((macro) => {
                    dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
                });
            });
            newDietPlan[day].totals = dailyTotals;

            setDietPlan(newDietPlan);
            setBaseMacrosFoodProportion((prev) => ({
                ...prev,
                [day]: {
                    ...prev[day],
                    [meal]: { ...prev[day][meal], foods: foodListCopy },
                },
            }));

            if (onDietChange) {
                onDietChange({ ...editedDiet, dietPlan: newDietPlan });
            }
        } else {
            getFoodsFilteredByName(newInputValue);
        }
    };

    /* ── Day navigation ───────────────────────────────────────────── */

    /** Updates the currently visible day in the diet table (0 = Monday … 6 = Sunday). */
    const handleDayChange = (day) => setSelectedDay(day);

    /* ═════════════════════════════════════════════════════════════════
       NUMERIC FIELD CHANGE — PROPORTIONAL MACRO RECALCULATION
       ═════════════════════════════════════════════════════════════════ */

    /**
     * Handles changes to any numeric field of a food item.
     *
     * NAME ENCODING
     * ─────────────
     * The `name` attribute of every `<TextField>` follows the pattern:
     *
     *     "{day}-{meal}-{foodIndex}-{field}"
     *
     * e.g. `"day0-breakfast-2-quantity"` means day 0, breakfast, 3rd food,
     * quantity field.
     *
     * QUANTITY — PROPORTIONAL RECALCULATION
     * ───────────────────────────────────
     * When `field === 'quantity'`:
     *
     *  1. Read the **base** quantity and macros from `baseMacrosFoodProportion`.
     *     These are the original values received from the server and never
     *     change (unless a different food is selected via Autocomplete).
     *
     *  2. Compute the ratio:
     *         ratio = newQuantity / baseQuantity
     *
     *  3. For each proportional macro (kcal, fats, proteins, carbs):
     *         newMacro = baseMacro × ratio
     *
     *     This way 200 g of a food that originally had 100 g will show
     *     exactly double the macros, regardless of how many intermediate
     *     edits the user made.
     *
     *  4. If `newQuantity` is 0, all proportional macros are set to 0 (the
     *     base values are intentionally left untouched so that raising
     *     quantity again restores the correct proportions).
     *
     * OTHER FIELDS
     * ────────────
     * For any other field (e.g. manually overriding kcal or fats) the value
     * is written directly into both `dietPlan` and `baseMacrosFoodProportion`,
     * since these are manual overrides that should become the new "base"
     * for future proportional calculations.
     *
     * TOTALS RECALCULATION
     * ────────────────────
     * After every change, meal-level totals are re-summed from the meal's
     * food list, and daily totals are re-summed from all meals of that day.
     *
     * PARENT NOTIFICATION
     * ───────────────────
     * If `onDietChange` is provided (edit mode inside EditDietModal), the
     * updated diet plan is forwarded to the parent so it can be saved.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const handleDietMealFoodChange = (e) => {
        const { name, value } = e.target;

        // ── 1. Decode the field path from the input name ──
        const [day, meal, index, field] = name.split('-');

        // ── 2. Clone the current plan (shallow — nested objects mutated in-place) ──
        const updatedDietPlan = { ...dietPlan };

        const parsedValue = (value === '' || isNaN(value)) ? value : parseFloat(value);

        // ── 3. Get the working food item AND its frozen base counterpart ──
        const foodItem = updatedDietPlan[day][meal].foods[index];
        const baseFoodItem = baseMacrosFoodProportion[day][meal].foods[index];

        if (field === 'quantity') {
            // ── 3a. Proportional recalculation for quantity ──
            const newQuantity = parsedValue;

            if (newQuantity > 0) {
                /*
                 * Ratio of new quantity to the original (base) quantity.
                 * All proportional macros are scaled by this ratio
                 * so their per-unit density stays constant.
                 */
                const ratio = newQuantity / baseFoodItem.quantity;

                foodItem.quantity = newQuantity;
                PROPORTIONAL_MACROS.forEach((macro) => {
                    foodItem[macro] = baseFoodItem[macro] * ratio;
                });
            } else {
                /*
                 * Zero quantity → display zeros, but do NOT touch baseFoodItem
                 * so that the proportions can be restored if quantity is raised again.
                 */
                foodItem.quantity = 0;
                PROPORTIONAL_MACROS.forEach((macro) => {
                    foodItem[macro] = 0;
                });
            }
        } else {
            // ── 3b. Direct override for non-quantity fields ──
            foodItem[field] = value;
            baseFoodItem[field] = value; // becomes the new "base" for this field
        }

        // ── 4. Recalculate meal totals ──
        if ([...ALL_MACROS, 'quantity'].includes(field)) {
            ALL_MACROS.forEach((macro) => {
                updatedDietPlan[day][meal][macro] = updatedDietPlan[day][meal].foods.reduce(
                    (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
                );
            });

            // ── 5. Recalculate daily totals ──
            const dailyTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));
            Object.keys(updatedDietPlan[day]).forEach((key) => {
                if (key === 'totals') return;
                ALL_MACROS.forEach((macro) => {
                    dailyTotals[macro] += parseFloat(updatedDietPlan[day][key][macro]) || 0;
                });
            });
            updatedDietPlan[day].totals = dailyTotals;
        }

        // ── 6. Commit state & notify parent ──
        setDietPlan(updatedDietPlan);
        if (onDietChange) {
            onDietChange({ ...editedDiet, dietPlan: updatedDietPlan });
        }
    };

    /* ── Diet CRUD operations ─────────────────────────────────────── */

    /** Permanently deletes the current diet via the API. */
    const deleteDiet = async () => {
        setLoading(true);
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/diets/${diet._id}`,
                { withCredentials: true }
            );
        } catch (err) {
            console.error(err);
        } finally {
            await onDeleteDiet();
            setShowDeleteDietConfirmationModal(false);
            setLoading(false);
        }
    };

    /**
     * Removes an entire meal from the selected day.
     * Recalculates daily totals and syncs both `dietPlan` and
     * `baseMacrosFoodProportion` state trees.
     */
    const deleteMeal = (meal, day) => {
        const newDietPlan = { ...dietPlan };
        const dailyDietCopy = Object.fromEntries(
            Object.entries(dietPlan[day]).filter(([key]) => key !== meal)
        );
        newDietPlan[day] = dailyDietCopy;

        // Recalculate daily totals
        const dailyTotals = Object.fromEntries(PROPORTIONAL_MACROS.map((m) => [m, 0]));
        Object.keys(newDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            PROPORTIONAL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
            });
        });
        newDietPlan[day].totals = dailyTotals;

        setDietPlan(newDietPlan);
        setBaseMacrosFoodProportion((prev) => ({ ...prev, [day]: dailyDietCopy }));

        if (onDietChange) {
            onDietChange({ ...editedDiet, dietPlan: newDietPlan });
        }
        setShowDeleteMealConfirmationModal(false);
    };

    /* ── Meal options menu ────────────────────────────────────────── */

    /** Opens the options dropdown menu (add food / delete meal) for a meal. */
    const handleToggleMealOptionsMenu = (meal, event) => {
        setMenuAnchorEls((prev) => ({ ...prev, [meal]: event.currentTarget }));
    };

    /** Closes the options dropdown menu for the given meal. */
    const handleCloseMealOptionsMenu = (meal) => {
        setMenuAnchorEls((prev) => ({ ...prev, [meal]: null }));
    };

    /* ── PDF download ─────────────────────────────────────────────── */

    /** Generates and saves a PDF of the current diet plan. */
    const handleDownloadDietPDF = () => {
        const doc = new jsPDF();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();
        let y = 25;
        const lineHeight = 8;
        const dietPreferredLanguage = diet?.dietLanguage ?? diet?.language ?? currentLanguage ?? 'en';
        const dietT = (key, options = {}) => t(key, { ...options, lng: dietPreferredLanguage });

        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(22);
        doc.text(capitalize(dietT('diet-plan')), 10, 15);
        doc.setFontSize(12);
        doc.setFont('Helvetica', 'normal');

        const addLine = (text) => {
            const lines = doc.splitTextToSize(text, 180);
            lines.forEach((line) => {
                if (y + lineHeight > pageHeight - 15) { doc.addPage(); y = 20; }
                doc.text(line, 10, y);
                y += lineHeight;
            });
        };

        const addTitleLine = (text) => {
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(14);
            addLine(text);
            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(12);
        };

        const addBoldLabelLine = (label, value) => {
            const labelText = `${label}: `;
            const labelWidth = doc.getTextWidth(labelText);
            if (y + lineHeight > pageHeight - 15) { doc.addPage(); y = 20; }
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(12);
            doc.text(labelText, 10, y);
            doc.setFont('Helvetica', 'normal');
            doc.text(String(value || ''), 10 + labelWidth + 8, y);
            y += lineHeight;
        };

        const resolveLocalizedField = (value) => {
            if (!value) return '';
            if (typeof value === 'string') return value;
            if (typeof value === 'object') {
                return value[dietPreferredLanguage] ?? value.en ?? Object.values(value)[0] ?? '';
            }
            return '';
        };

        const resolveFoodName = (food) => resolveLocalizedField(food?.name);
        const resolveFoodDescription = (food) => resolveLocalizedField(food?.description);

        const recipeSummaries = new Map();
        const registerRecipeSummary = (food, displayName) => {
            if (!displayName) return;
            const key = food.recipeId ?? `${displayName}-${food.preparationTime ?? ''}`;
            if (!recipeSummaries.has(key)) {
                recipeSummaries.set(key, {
                    name: displayName,
                    description: resolveFoodDescription(food),
                    preparationTime: food.preparationTime,
                });
            }
        };

        const dietWeekDayNames = [
            'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
        ].map((labelKey) => dietT(labelKey).toLowerCase());

        addLine(`${capitalize(dietT('client'))}: ${client.name} ${client.surname}`);
        addLine(`${capitalize(dietT('start-date'))}: ${new Date(diet.startDate).toLocaleDateString()}`);
        addLine(`${capitalize(dietT('end-date'))}: ${new Date(diet.endDate).toLocaleDateString()}`);
        addLine('');

        for (const [day, dailyDiet] of Object.entries(diet.dietPlan)) {
            addLine('');
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(18);
            const dayIndex = Number(day.charAt(day.length - 1));
            const dayLabel = dietWeekDayNames[dayIndex]?.toUpperCase() ?? '';
            const textWidth = doc.getTextWidth(dayLabel);
            if (y + lineHeight > pageHeight - 15) { doc.addPage(); y = 20; }
            doc.text(dayLabel, (pageWidth - textWidth) / 2, y);
            y += lineHeight;

            MEAL_ORDER.forEach((meal) => {
                if (!dailyDiet[meal]) return;
                addLine('');
                doc.setFont('Helvetica', 'bold');
                doc.setFontSize(16);
                addLine(dietT(meal).toUpperCase());
                dailyDiet[meal].foods.forEach((food) => {
                    doc.setFont('Helvetica', 'normal');
                    doc.setFontSize(16);
                    const foodName = resolveFoodName(food);
                    if (food.type === 'recipe') {
                        registerRecipeSummary(food, foodName);
                        const servingsValue = Number(food.servings ?? 1) || 1;
                        const quantityLabel = servingsValue === 1 ? dietT('far-serving') : dietT('rm-servings');
                        addLine(`${foodName} ${servingsValue} ${quantityLabel}`);
                    } else {
                        addLine(`${foodName} ${food.quantity}gr`);
                    }
                });
            });
        }

        if (recipeSummaries.size > 0) {
            addLine('');
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(18);
            addLine(capitalizeFirst(dietT('recipes')));
            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(12);
            recipeSummaries.forEach((summary) => {
                if (!summary.name) return;
                addTitleLine(summary.name);
                if (summary.preparationTime) {
                    addBoldLabelLine(dietT('rm-preparation-time'), `${summary.preparationTime} min`);
                }
                if (summary.description) {
                    addLine(summary.description);
                }
                addLine('');
            });
        }

        doc.save(`${dietT('diet')}-${client.name}-${client.surname}.pdf`);
    };

    /**
     * Removes a single food item from a meal on a given day.
     * If removing the food leaves the meal empty, falls back to `deleteMeal`.
     * Otherwise, meal and daily totals are recalculated, and both `dietPlan`
     * and `baseMacrosFoodProportion` are updated in lockstep.
     */
    const deleteFood = (day, meal, index) => {
        const foodListCopy = [...dietPlan[day][meal].foods];
        foodListCopy.splice(index, 1);

        if (foodListCopy.length === 0) {
            deleteMeal(meal, day);
            return;
        }

        const newDietPlan = { ...dietPlan };
        newDietPlan[day][meal].foods = foodListCopy;

        // Recalculate meal totals
        PROPORTIONAL_MACROS.forEach((macro) => {
            newDietPlan[day][meal][macro] = foodListCopy.reduce(
                (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
            );
        });

        // Recalculate daily totals
        const dailyTotals = Object.fromEntries(PROPORTIONAL_MACROS.map((m) => [m, 0]));
        Object.keys(newDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            PROPORTIONAL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
            });
        });
        newDietPlan[day].totals = dailyTotals;

        setDietPlan(newDietPlan);
        setBaseMacrosFoodProportion((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [meal]: { ...prev[day][meal], foods: foodListCopy },
            },
        }));

        if (onDietChange) {
            onDietChange({ ...editedDiet, dietPlan: newDietPlan });
        }
    };

    /* ── Autocomplete food selection ──────────────────────────────── */

    /**
     * Handles selecting (or clearing) a food from the Autocomplete dropdown.
     * Replaces the food at the given index, recalculates totals, and
     * updates both `dietPlan` and `baseMacrosFoodProportion`.
     */
    const handleFoodOptionSelect = (event, value, day, meal, index) => {
        if (event.type !== 'click') return;

        const selectedFood = !value
            ? { name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0, carbs: 0 }
            : typeof value === 'object'
                ? value
                : foodSearchList.find((f) => f.name[currentLanguage] === value);

        const foodListCopy = [...dietPlan[day][meal].foods];
        foodListCopy[index] = selectedFood;

        const newDietPlan = { ...dietPlan };
        newDietPlan[day][meal].foods = foodListCopy;

        PROPORTIONAL_MACROS.forEach((macro) => {
            newDietPlan[day][meal][macro] = foodListCopy.reduce(
                (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
            );
        });

        const dailyTotals = Object.fromEntries(PROPORTIONAL_MACROS.map((m) => [m, 0]));
        Object.keys(newDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            PROPORTIONAL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
            });
        });
        newDietPlan[day].totals = dailyTotals;

        setDietPlan(newDietPlan);
        setBaseMacrosFoodProportion((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [meal]: { ...prev[day][meal], foods: foodListCopy },
            },
        }));

        if (onDietChange) {
            onDietChange({ ...editedDiet, dietPlan: newDietPlan });
        }
        setFoodSearchList([]);
    };

    /** Adds a blank food row to the given meal (called from the meal options menu). */
    const handleAddFoodToMeal = (day, meal) => {
        const blankFood = { name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0, carbs: 0 };
        const dailyFoods = [...dietPlan[day][meal].foods, blankFood];

        const updateFoods = (prev) => ({
            ...prev,
            [day]: { ...prev[day], [meal]: { ...prev[day][meal], foods: dailyFoods } },
        });

        setDietPlan(updateFoods);
        setBaseMacrosFoodProportion(updateFoods);

        if (onDietChange) {
            onDietChange({
                ...editedDiet,
                dietPlan: {
                    ...editedDiet.dietPlan,
                    [day]: {
                        ...editedDiet.dietPlan[day],
                        [meal]: { ...editedDiet.dietPlan[day][meal], foods: dailyFoods },
                    },
                },
            });
        }
        handleCloseMealOptionsMenu(meal);
    };

    const handleAddRecipe = (day, meal) => {
        setRecipeTarget({ day, meal });
        setShowRecipePicker(true);
        handleCloseMealOptionsMenu(meal);
    };

    const handleSelectRecipe = (recipe) => {
        if (!recipeTarget?.day || !recipeTarget?.meal) return;

        const { day, meal } = recipeTarget;
        const targetMeal = dietPlan[day]?.[meal];
        if (!targetMeal) {
            setShowRecipePicker(false);
            setRecipeTarget({ day: null, meal: null });
            return;
        }
        const newFood = {
            name: recipe?.name ?? { en: '', it: '' },
            quantity: recipe?.servings ?? 1,
            kcal: Number(recipe?.kcal ?? 0),
            fats: Number(recipe?.fats ?? 0),
            proteins: Number(recipe?.proteins ?? 0),
            carbs: Number(recipe?.carbs ?? 0),
            sugars: Number(recipe?.sugars ?? 0),
            fibers: Number(recipe?.fibers ?? 0),
            salt: Number(recipe?.salt ?? 0),
            recipeId: recipe?._id,
            type: 'recipe',
            servings: Number(recipe?.servings ?? 1),
            description: recipe?.description ?? '',
            preparationTime: recipe?.preparationTime ?? '',
        };

        const updatedFoods = [...targetMeal.foods, newFood];
        const updatedDietPlan = {
            ...dietPlan,
            [day]: {
                ...dietPlan[day],
                [meal]: { ...targetMeal, foods: updatedFoods },
            },
        };

        const updatedMeal = updatedDietPlan[day][meal];
        ALL_MACROS.forEach((macro) => {
            updatedMeal[macro] = updatedFoods.reduce(
                (sum, item) => sum + (parseFloat(item[macro]) || 0),
                0
            );
        });

        const dailyTotals = Object.fromEntries(ALL_MACROS.map((macro) => [macro, 0]));
        Object.keys(updatedDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            ALL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(updatedDietPlan[day][key][macro]) || 0;
            });
        });
        updatedDietPlan[day].totals = dailyTotals;

        setDietPlan(updatedDietPlan);
        setBaseMacrosFoodProportion((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [meal]: JSON.parse(JSON.stringify(updatedMeal)),
            },
        }));

        if (onDietChange) {
            onDietChange({
                ...editedDiet,
                dietPlan: {
                    ...editedDiet.dietPlan,
                    [day]: updatedDietPlan[day],
                },
            });
        }

        setShowRecipePicker(false);
        setRecipeTarget({ day: null, meal: null });
    };

    /* ═══════════════════════════════════════════════════════════════
       RENDER HELPERS
       ═══════════════════════════════════════════════════════════════ */

    /**
     * Renders a compact numeric `<TextField>` for a food-row macro cell.
     *
     * @param {string} fieldName - Encoded as `{day}-{meal}-{index}-{field}`.
     * @param {number} value     - Current numeric value.
     * @param {number} [max=9999]- Maximum allowed value.
     */
    const renderNumericField = (fieldName, value, max = 9999, decimals = null) => (
        <TextField
            name={fieldName}
            type="number"
            value={!value ? '' : (decimals !== null ? Number(Number(value).toFixed(decimals)) : Number(value))}
            placeholder="0"
            disabled={!editing}
            onChange={(e) => {
                const clamped = Math.min(Number(e.target.value), max);
                handleDietMealFoodChange({
                    ...e,
                    target: { ...e.target, value: clamped, name: e.target.name },
                });
            }}
            sx={numFieldSx}
        />
    );

    /**
     * Renders the Popper-based options menu for a meal header row
     * (Add food / Delete meal).
     */
    const renderMealOptionsMenu = (meal, day) => (
        <div>
            <IconButton
                size="small"
                id={`composition-button-${meal}`}
                aria-controls={menuAnchorEls[meal] ? `composition-menu-${meal}` : undefined}
                aria-expanded={menuAnchorEls[meal] ? 'true' : undefined}
                aria-haspopup="true"
                onClick={(e) => handleToggleMealOptionsMenu(meal, e)}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Popper
                open={Boolean(menuAnchorEls[meal])}
                anchorEl={menuAnchorEls[meal]}
                placement="bottom-start"
                transition
                style={{ zIndex: 1000 }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom' }}
                    >
                        <Paper elevation={3} sx={{ backgroundColor: '#fff' }}>
                            <ClickAwayListener onClickAway={() => handleCloseMealOptionsMenu(meal)}>
                                <MenuList
                                    autoFocusItem={Boolean(menuAnchorEls[meal])}
                                    id={`composition-menu-${meal}`}
                                >
                                    <MenuItem onClick={() => handleAddFoodToMeal(day, meal)}>
                                        <LunchDiningIcon fontSize="small" sx={{ mr: 1 }} />
                                        {capitalizeFirst(t('add-food'))}
                                    </MenuItem>
                                    <MenuItem onClick={() => handleAddRecipe(day, meal)}>
                                        <RestaurantIcon fontSize="small" sx={{ mr: 1 }} />
                                        {capitalizeFirst(t('add-recipe'))}
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setDeleteMealConfirmationModalOptions({ meal, day });
                                            setShowDeleteMealConfirmationModal(true);
                                            handleCloseMealOptionsMenu(meal);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                        {capitalizeFirst(t('delete-meal'))}
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );

    /** Formats a macro value to a fixed number of decimal places. */
    const fmtMacro = (val, decimals = 1) =>
        val != null ? Number(val.toFixed(decimals)) : 0;

    /**
     * Builds the array of `<TableRow>` elements for the currently selected day.
     * Each meal gets a coloured header row (with totals), followed by one
     * row per food item. In edit mode, food rows are editable and meal
     * headers include an options menu.
     */
    const renderTableRows = () => {
        const day = 'day' + selectedDay;
        const rows = [];
        if (!dietPlan[day]) return rows;
        let firstMealRow = true;

        MEAL_ORDER.forEach((meal) => {
            if (!dietPlan[day][meal]) return;
            const mealData = dietPlan[day][meal];

            /* ── Meal header row (background-highlighted) ──────────── */
            rows.push(
                <TableRow key={`${day}-${meal}`} className="dt-meal-row" {...(firstMealRow ? { 'data-tour': 'dt-meal-header' } : {})}>
                    <TableCell className="dt-cell">
                        <span className="dt-meal-name">{t(meal).toUpperCase()}</span>
                    </TableCell>
                    <TableCell className="dt-cell" align="right" />
                    <TableCell className="dt-cell dt-col-kcal" align="right">
                        <b>{Math.round(mealData.kcal ?? 0)}</b>
                    </TableCell>
                    <TableCell className="dt-cell" align="right">
                        <b>{fmtMacro(mealData.fats)}</b>
                    </TableCell>
                    <TableCell className="dt-cell" align="right">
                        <b>{fmtMacro(mealData.carbs)}</b>
                    </TableCell>
                    <TableCell className="dt-cell dt-col-detail" align="right">
                        <b>{fmtMacro(mealData.sugars)}</b>
                    </TableCell>
                    <TableCell className="dt-cell dt-col-detail" align="right">
                        <b>{fmtMacro(mealData.fibers)}</b>
                    </TableCell>
                    <TableCell className="dt-cell" align="right">
                        <b>{fmtMacro(mealData.proteins)}</b>
                    </TableCell>
                    <TableCell className="dt-cell dt-col-detail" align="right">
                        <b>{fmtMacro(mealData.salt)}</b>
                    </TableCell>
                    {editing && (
                        <TableCell className="dt-cell">
                            {renderMealOptionsMenu(meal, day)}
                        </TableCell>
                    )}
                </TableRow>
            );

            firstMealRow = false;
            /* ── Food rows ─────────────────────────────────────────── */
            mealData.foods.forEach((food, index) => {
                const namePrefix = `${day}-${meal}-${index}`;
                rows.push(
                    <TableRow key={namePrefix} className="dt-food-row">
                        {/* Food name (Autocomplete) */}
                        <TableCell className="dt-cell">
                            <Autocomplete
                                id={`${namePrefix}-name`}
                                className="dt-food-name-input"
                                value={food.name?.[currentLanguage] || food.name || ''}
                                freeSolo
                                disableClearable={!editing}
                                disabled={!editing}
                                onChange={(e, val) => handleFoodOptionSelect(e, val, day, meal, index)}
                                options={foodSearchList.filter((f) => f.name?.[currentLanguage])}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.name?.[currentLanguage] || '';
                                }}
                                onInputChange={(event, newInputValue, reason) =>
                                    handleFoodInputChange(event, newInputValue, reason, day, meal, index)
                                }
                                renderOption={(props, option) => {
                                    const name = option.name?.[currentLanguage] || '';
                                    const brand = option.brand;
                                    const brandDisplay = brand
                                        ? (typeof brand === 'object'
                                            ? (brand?.[currentLanguage] ?? brand?.en ?? '')
                                            : brand)
                                        : null;
                                    return (
                                        <li {...props} key={option._id}>
                                            {name}
                                            {brandDisplay && (
                                                <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                    {brandDisplay}
                                                </span>
                                            )}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                    />
                                )}
                                filterOptions={(x) => x}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': { borderColor: appColors.tertiary },
                                        '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                                    },
                                }}
                            />
                        </TableCell>

                        {/* Quantity */}
                        <TableCell className="dt-cell" align="right">
                            {renderNumericField(`${namePrefix}-quantity`, food.quantity, 9999)}
                        </TableCell>

                        {/* Kcal — hidden on small screens (≤ 768px) */}
                        <TableCell className="dt-cell dt-col-kcal" align="right">
                            {renderNumericField(`${namePrefix}-kcal`, food.kcal, 99999)}
                        </TableCell>

                        {/* Fats — always visible */}
                        <TableCell className="dt-cell" align="right">
                            {renderNumericField(`${namePrefix}-fats`, food.fats, 9999, 1)}
                        </TableCell>

                        {/* Carbs — always visible */}
                        <TableCell className="dt-cell" align="right">
                            {renderNumericField(`${namePrefix}-carbs`, food.carbs, 9999, 1)}
                        </TableCell>

                        {/* Sugars — hidden on medium screens (≤ 1024px) */}
                        <TableCell className="dt-cell dt-col-detail" align="right">
                            {renderNumericField(`${namePrefix}-sugars`, food.sugars, 9999, 1)}
                        </TableCell>

                        {/* Fibers — hidden on medium screens (≤ 1024px) */}
                        <TableCell className="dt-cell dt-col-detail" align="right">
                            {renderNumericField(`${namePrefix}-fibers`, food.fibers, 9999, 1)}
                        </TableCell>

                        {/* Proteins — always visible */}
                        <TableCell className="dt-cell" align="right">
                            {renderNumericField(`${namePrefix}-proteins`, food.proteins, 9999, 1)}
                        </TableCell>

                        {/* Salt — hidden on medium screens (≤ 1024px) */}
                        <TableCell className="dt-cell dt-col-detail" align="right">
                            {renderNumericField(`${namePrefix}-salt`, food.salt, 9999, 1)}
                        </TableCell>

                        {/* Delete button (edit mode only) */}
                        {editing && (
                            <TableCell className="dt-cell">
                                <div
                                    className="dt-delete-btn"
                                    onClick={() => deleteFood(day, meal, index)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </div>
                            </TableCell>
                        )}
                    </TableRow>
                );
            });
        });

        return rows;
    };

    /* ═══════════════════════════════════════════════════════════════
       JSX
       ═══════════════════════════════════════════════════════════════ */

    const dayKey = 'day' + selectedDay;
    const totals = dietPlan[dayKey]?.totals;

    return (
        <>
            <LoadingOverlay open={loading} />

            <div className="dt-wrapper">
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                </div>
                {/* ── Info bar (dates & actions) ────────────────────── */}
                {showInfoBar && (
                    <div className="dt-info" data-tour="dt-info">
                        {dietGeneration ? (
                            <div className="dt-info-dates">
                                <p>
                                    <b>{t('start-date')}:</b>{' '}
                                    {dietGeneration.startDate
                                        ? formatDate(getFullDate(dietGeneration.startDate), 'dd-mm-yyyy')
                                        : '-'}
                                </p>
                                <p>
                                    <b>{t('end-date')}:</b>{' '}
                                    {dietGeneration.endDate
                                        ? formatDate(getFullDate(dietGeneration.endDate), 'dd-mm-yyyy')
                                        : '-'}
                                </p>
                                {dietGeneration.dietType && (
                                    <p>
                                        <b>{capitalizeFirst(t('diet-type'))}:</b>{' '}
                                        {capitalizeFirst(t(dietGeneration.dietType))}
                                    </p>
                                )}
                            </div>
                        ) : !editing && (
                            <>
                                <div className="dt-info-dates">
                                    <p>
                                        <b>{t('start-date')}:</b>{' '}
                                        {diet?.startDate
                                            ? formatDate(getFullDate(diet.startDate), 'dd-mm-yyyy')
                                            : '-'}
                                    </p>
                                    <p>
                                        <b>{t('end-date')}:</b>{' '}
                                        {diet?.endDate
                                            ? formatDate(getFullDate(diet.endDate), 'dd-mm-yyyy')
                                            : '-'}
                                    </p>
                                    {diet?.dietType && (
                                        <p>
                                            <b>{capitalizeFirst(t('diet-type'))}:</b>{' '}
                                            {capitalizeFirst(t(diet.dietType))}
                                        </p>
                                    )}
                                </div>
                                <div className="dt-info-actions">
                                    <Tooltip title={t('Edit')}>
                                        <div className="dt-action-btn" onClick={() => setShowEditDietModal(true)}>
                                            <EditNoteIcon fontSize="small" />
                                            <p>{t('Edit')}</p>
                                        </div>
                                    </Tooltip>
                                    <Tooltip title={t('Download')}>
                                        <div className="dt-action-btn" onClick={handleDownloadDietPDF}>
                                            <DownloadIcon fontSize="small" />
                                            <p>{t('Download')}</p>
                                        </div>
                                    </Tooltip>
                                    <Tooltip title={capitalize(t('delete'))}>
                                        <div className="dt-action-btn" onClick={() => setShowDeleteDietConfirmationModal(true)}>
                                            <DeleteIcon fontSize="small" />
                                            <p>{capitalize(t('delete'))}</p>
                                        </div>
                                    </Tooltip>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── Day selector ─────────────────────────────────── */}
                <div className="dt-day-selector" data-tour="dt-day-selector">
                    <button
                        className="dt-nav-btn"
                        disabled={selectedDay === 0 || !!dietGeneration}
                        onClick={() => handleDayChange(selectedDay - 1)}
                    >
                        <ChevronLeftIcon />
                    </button>

                    <ButtonGroup variant="outlined" disabled={!!dietGeneration}>
                        {weekDayNames.map((name, i) => (
                            <Button
                                key={i}
                                onClick={() => handleDayChange(i)}
                                className={`dt-day-btn ${selectedDay === i ? 'dt-day-btn--active' : ''}`}
                            >
                                {name}
                            </Button>
                        ))}
                    </ButtonGroup>

                    <button
                        className="dt-nav-btn"
                        disabled={selectedDay === 6 || !!dietGeneration}
                        onClick={() => handleDayChange(selectedDay + 1)}
                    >
                        <ChevronRightIcon />
                    </button>
                </div>

                {/* ── Generic brand filter (edit mode only) ────────── */}
                {editing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pb: 1 }}>
                        <Tooltip
                            title={t('only-generic-brand-info')}
                            arrow
                            placement="top"
                        >
                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#94a3b8', mr: 0.5, cursor: 'default' }} />
                        </Tooltip>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={onlyGenerics}
                                    onChange={(e) => {
                                        setOnlyGenerics(e.target.checked);
                                        setFoodSearchList([]);
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: appColors.secondary },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: appColors.secondary },
                                    }}
                                />
                            }
                            label={<span style={{ fontSize: '0.85rem' }}>{t("only-generic-brand")}</span>}
                            sx={{ m: 0 }}
                        />
                    </Box>
                )}

                {/* ── Diet table ───────────────────────────────────── */}
                <TableContainer component={Paper} className="dt-table-container" data-tour="dt-table-container">
                    <Table size="small" aria-label="diet table">
                        <TableHead>
                            <TableRow>
                                <TableCell className="dt-header-cell" sx={{ minWidth: 200 }}>
                                    {editing && (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            size="small"
                                            onClick={() => setShowAddMealModal(true)}
                                            sx={{
                                                backgroundColor: appColors.primary,
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                ':hover': { backgroundColor: appColors.secondary },
                                            }}
                                        >
                                            {t('add-new-meal')}
                                        </Button>
                                    )}
                                </TableCell>
                                <TableCell className="dt-header-cell" align="right">
                                    {t('Quantity').toUpperCase()}
                                    <br /><span style={{ fontSize: '0.7rem' }}>(gr/ml)</span>
                                </TableCell>
                                <TableCell className="dt-header-cell dt-col-kcal" align="right">
                                    {t('kcal').toUpperCase()}
                                </TableCell>
                                <TableCell className="dt-header-cell" align="right">
                                    {t('Fats').toUpperCase()}
                                </TableCell>
                                <TableCell className="dt-header-cell" align="right">
                                    {t('Carbs').toUpperCase()}
                                </TableCell>
                                <TableCell className="dt-header-cell dt-col-detail" align="right">
                                    {t('sugars').toUpperCase()}
                                </TableCell>
                                <TableCell className="dt-header-cell dt-col-detail" align="right">
                                    {t('fibers').toUpperCase()}
                                </TableCell>
                                <TableCell className="dt-header-cell" align="right">
                                    {t('Proteins').toUpperCase()}
                                </TableCell>
                                <TableCell className="dt-header-cell dt-col-detail" align="right">
                                    {t('salt').toUpperCase()}
                                </TableCell>
                                {editing && <TableCell className="dt-header-cell" />}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dietGeneration ? (
                                /* Progress bar during AI diet generation */
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ border: 0, py: 4 }}>
                                        <Box sx={{ width: '80%', mx: 'auto', mb: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={dietGeneration.progress ?? 0}
                                                sx={{
                                                    backgroundColor: appColors.tertiary,
                                                    height: 20,
                                                    borderRadius: 5,
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: appColors.secondary,
                                                        borderRadius: 5,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <span>
                                            {t('generating-diet')}... 
                                            {dietGeneration.progress
                                                ? `${Math.round(dietGeneration.progress)}%`
                                                : '0%'}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {/* Daily total row */}
                                    <TableRow className="dt-total-row" data-tour="dt-total-row">
                                        <TableCell className="dt-cell"><b>{t('Total')}</b></TableCell>
                                        <TableCell className="dt-cell" align="right" />
                                        <TableCell className="dt-cell dt-col-kcal" align="right">
                                            <b>{totals ? Math.round(totals.kcal) : 0}</b>
                                        </TableCell>
                                        <TableCell className="dt-cell" align="right">
                                            <b>{totals ? fmtMacro(totals.fats) : 0}</b>
                                        </TableCell>
                                        <TableCell className="dt-cell" align="right">
                                            <b>{totals ? fmtMacro(totals.carbs) : 0}</b>
                                        </TableCell>
                                        <TableCell className="dt-cell dt-col-detail" align="right">
                                            <b>{totals ? fmtMacro(totals.sugars) : 0}</b>
                                        </TableCell>
                                        <TableCell className="dt-cell dt-col-detail" align="right">
                                            <b>{totals ? fmtMacro(totals.fibers) : 0}</b>
                                        </TableCell>
                                        <TableCell className="dt-cell" align="right">
                                            <b>{totals ? fmtMacro(totals.proteins) : 0}</b>
                                        </TableCell>
                                        <TableCell className="dt-cell dt-col-detail" align="right">
                                            <b>{totals ? fmtMacro(totals.salt) : 0}</b>
                                        </TableCell>
                                        {editing && <TableCell className="dt-cell" />}
                                    </TableRow>
                                    {renderTableRows()}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* ── Modals ──────────────────────────────────────────── */}
            {showEditDietModal && (
                <EditDietModal
                    setShowModal={setShowEditDietModal}
                    diet={diet}
                    onSave={refreshDietTable}
                />
            )}
            {showAddMealModal && (
                <AddMealModal
                    setShowModal={setShowAddMealModal}
                    dietPlan={dietPlan}
                    setDietPlan={setDietPlan}
                    selectedDay={'day' + selectedDay}
                    setOriginalDietFoodProportion={setBaseMacrosFoodProportion}
                    handleEditedDiet={onDietChange}
                    editedDiet={editedDiet}
                />
            )}
            {showRecipePicker && (
                <RecipePickerModal
                    open={showRecipePicker}
                    dietistId={dietist?._id}
                    language={currentLanguage}
                    onClose={() => {
                        setShowRecipePicker(false);
                        setRecipeTarget({ day: null, meal: null });
                    }}
                    onSelect={handleSelectRecipe}
                />
            )}
            {showDeleteDietConfirmationModal && (
                <ConfirmationModal
                    id="delete-diet-confirmation-modal"
                    titleText={capitalize(t('delete-diet'))}
                    bodyText={capitalize(t('confirm-delete-diet'))}
                    handleConfirm={deleteDiet}
                    handleClose={() => setShowDeleteDietConfirmationModal(false)}
                />
            )}
            {showDeleteMealConfirmationModal && (
                <ConfirmationModal
                    id="delete-meal-confirmation-modal"
                    titleText={`${t('delete-meal')}: ${t(deleteMealConfirmationModalOptions.meal)}`}
                    bodyText={t('confirm-delete-meal')}
                    handleConfirm={() =>
                        deleteMeal(
                            deleteMealConfirmationModalOptions.meal,
                            deleteMealConfirmationModalOptions.day
                        )
                    }
                    handleClose={() => setShowDeleteMealConfirmationModal(false)}
                />
            )}
        </>
    );
};

export default DietTable;